/*
 * Cartzy order tracking — storefront script ("Dr. Rao's Premium Order
 * Tracking", Phase 1).
 *
 * Posts { identifier, phone } to the SAME-ORIGIN Shopify app proxy path
 * (<proxy path>/track/lookup). Shopify signs and forwards the request to
 * Cartzy. Nothing here knows a key, a token or the backend's address.
 *
 * Rules this file keeps:
 *   - the mobile number travels ONLY in the POST body, never in a URL;
 *   - server data is written with textContent only (never parsed as HTML), so
 *     nothing in a response can become markup;
 *   - one automatic retry, and only for a network failure or timeout — never
 *     for a "not found", which would spend one of the customer's attempts;
 *   - the submit button's LABEL text is never rewritten (loading is shown
 *     with a separate spinner), so the theme editor can never copy anything
 *     into the "Button label" setting;
 *   - everything shown is derived from the fields the lookup already returns;
 *     nothing is invented.
 */
(function () {
  'use strict';

  var LOOKUP_PATH = '/track/lookup';
  var TIMEOUT_MS = 15000;
  var TIME_ZONE = 'Asia/Kolkata';
  var HISTORY_MIN = 3;
  var HISTORY_MAX = 6;
  var HISTORY_DEFAULT = 4;

  // ---------------------------------------------------------------------------
  // The approved five-stage journeys, and how a parcel's customer status (and
  // its own timeline) place it on them. A stage is only ever shown as reached
  // when the parcel's current status or one of its stored updates says so.
  // ---------------------------------------------------------------------------
  var FORWARD_STAGES = ['order_received', 'shipped', 'in_transit', 'out_for_delivery', 'delivered'];
  var RETURN_STAGES = ['order_received', 'shipped', 'in_transit', 'returning_to_sender', 'returned_to_sender'];
  var STAGE_LABELS = {
    order_received: 'Order received',
    shipped: 'Shipped',
    in_transit: 'In transit',
    out_for_delivery: 'Out for delivery',
    delivered: 'Delivered',
    returning_to_sender: 'Returning to sender',
    returned_to_sender: 'Returned to sender'
  };
  var FORWARD_STAGE_OF = {
    booked: 1, shipped: 1,
    in_transit: 2, at_delivery_office: 2, held_at_post_office: 2,
    out_for_delivery: 3, delivery_attempted: 3,
    delivered: 4
  };
  var RETURN_STAGE_OF = { returning_to_sender: 3, returned_to_sender: 4 };
  // A stored update that names a final outcome. On a parcel Cartzy has
  // settled (status delivered / returned_to_sender) it agrees with the
  // status; on an unsettled one it conflicts with something — the backend
  // reports `inconsistent` exactly when it saw two different final outcomes.
  var FINAL_STEPS = { delivered: true, returned_to_sender: true };
  // Shown instead of a stepper when an unsettled parcel's own updates name a
  // final outcome: no outcome is inferred.
  var UNSETTLED_TITLE = 'Tracking status is being updated';
  var ATTENTION = { held_at_post_office: true, delivery_attempted: true, returning_to_sender: true };
  var NEUTRAL = { returned_to_sender: true, update_pending: true, cancelled: true };

  var SUBLINES = {
    order_received: "We've received your order. Tracking will appear here once it ships.",
    shipped: 'Your parcel has been handed to India Post.',
    in_transit: 'Your parcel is on its way.',
    at_delivery_office: 'Your parcel has reached your delivery post office.',
    held_at_post_office: 'Your parcel is being held at the post office. Please contact us if you need help.',
    out_for_delivery: 'Your parcel is out for delivery.',
    delivery_attempted: 'India Post tried to deliver your parcel. Please contact us if you need help.',
    delivered: 'Your parcel has been delivered.',
    returning_to_sender: 'This parcel is on its way back to us. Please contact us if you need help.',
    returned_to_sender: 'This parcel has been returned to us. Please contact us if you need help.',
    update_pending: "We're waiting for the next update from India Post.",
    cancelled: 'This order has been cancelled. Please contact us if you have any questions.'
  };
  var NEEDS_CONTACT = { held_at_post_office: true, delivery_attempted: true, returning_to_sender: true, returned_to_sender: true, cancelled: true };

  function has(map, key) {
    return Object.prototype.hasOwnProperty.call(map, key);
  }

  function furthest(map, steps) {
    var best = -1;
    for (var i = 0; i < steps.length; i++) {
      var key = steps[i] && steps[i].status;
      if (has(map, key) && map[key] > best) best = map[key];
    }
    return best;
  }

  function namesFinalOutcome(steps) {
    for (var i = 0; i < steps.length; i++) {
      if (steps[i] && has(FINAL_STEPS, steps[i].status)) return true;
    }
    return false;
  }

  function toneFor(status) {
    if (has(ATTENTION, status)) return 'attention';
    if (has(NEUTRAL, status)) return 'neutral';
    return 'positive';
  }

  // Where ONE parcel sits on its journey, from its customer status and its
  // own timeline only. `current` is the index of the stage it has reached;
  // every earlier stage is reached by definition; `complete` is true only for
  // a delivered (or returned) parcel.
  //
  // update_pending — or a status this file was not taught — is a parcel
  // Cartzy has NOT settled. If its stored updates name a final outcome
  // (delivered / returned to sender) they conflict with something, so there
  // is no stepper at all (null) rather than a guessed outcome. Otherwise it
  // uses the furthest stage its stored updates prove — which, with no final
  // step among them, is never a final stage — and else "Shipped" (a parcel
  // row only exists once the order shipped).
  function progressFor(status, timeline) {
    var steps = Array.isArray(timeline) ? timeline : [];
    var journey;
    var current;
    if (has(RETURN_STAGE_OF, status)) {
      journey = 'return';
      current = RETURN_STAGE_OF[status];
    } else if (has(FORWARD_STAGE_OF, status)) {
      journey = 'forward';
      current = FORWARD_STAGE_OF[status];
    } else {
      if (namesFinalOutcome(steps)) return null;
      var back = furthest(RETURN_STAGE_OF, steps);
      if (back >= 0) {
        journey = 'return';
        current = back;
      } else {
        journey = 'forward';
        current = Math.max(furthest(FORWARD_STAGE_OF, steps), 1);
      }
    }
    return {
      journey: journey,
      stages: journey === 'return' ? RETURN_STAGES : FORWARD_STAGES,
      current: current,
      complete: (journey === 'forward' && status === 'delivered') ||
        (journey === 'return' && status === 'returned_to_sender'),
      tone: toneFor(status)
    };
  }

  // An order the lookup found but that has no parcel yet.
  function progressForOrderOnly() {
    return { journey: 'forward', stages: FORWARD_STAGES, current: 0, complete: false, tone: 'positive' };
  }

  // ---------------------------------------------------------------------------
  // Small DOM helpers — text only, never markup.
  // ---------------------------------------------------------------------------
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function format(iso, withTime) {
    if (!iso) return '';
    var date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    var options = { timeZone: TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric' };
    if (withTime) { options.hour = 'numeric'; options.minute = '2-digit'; }
    try {
      return new Intl.DateTimeFormat('en-IN', options).format(date);
    } catch (e) {
      return withTime ? date.toLocaleString() : date.toLocaleDateString();
    }
  }

  function safeHttpUrl(value) {
    if (!value) return null;
    try {
      var url = new URL(value, window.location.origin);
      return (url.protocol === 'https:' || url.protocol === 'http:') ? url.href : null;
    } catch (e) {
      return null;
    }
  }

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  var instances = 0;

  function CartzyTrack(root) {
    this.root = root;
    this.uid = 'CartzyTrack' + (++instances);
    this.form = root.querySelector('[data-cartzy-track-form]');
    this.search = root.querySelector('[data-cartzy-track-search]');
    this.intro = root.querySelector('[data-cartzy-track-intro]');
    this.message = root.querySelector('[data-cartzy-track-message]');
    this.result = root.querySelector('[data-cartzy-track-result]');
    this.button = this.form.querySelector('button[type="submit"]');
    var path = (root.getAttribute('data-proxy-path') || '/apps/cartzy').trim();
    // Same-origin paths only: a full URL here would send the customer's
    // mobile number to another site.
    this.endpoint = (path.charAt(0) === '/' && path.charAt(1) !== '/' ? path.replace(/\/+$/, '') : '/apps/cartzy') + LOOKUP_PATH;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('input', this.onInput.bind(this));
    this.prefill();
  }

  CartzyTrack.prototype.attr = function (name) {
    return this.root.getAttribute('data-' + name) || '';
  };

  CartzyTrack.prototype.label = function (key, fallback) {
    return this.attr('label-' + key) || fallback || STAGE_LABELS[key] || key;
  };

  CartzyTrack.prototype.showProgress = function () {
    return this.attr('show-progress') !== 'false';
  };

  CartzyTrack.prototype.historyCount = function () {
    var n = parseInt(this.attr('history-count'), 10);
    if (isNaN(n)) return HISTORY_DEFAULT;
    return Math.min(HISTORY_MAX, Math.max(HISTORY_MIN, n));
  };

  // A link may carry the order number (never the mobile number), e.g.
  // /pages/track-order?order=DR1234ON.
  CartzyTrack.prototype.prefill = function () {
    try {
      var params = new URLSearchParams(window.location.search);
      var value = params.get('order') || params.get('tracking');
      if (value) this.form.elements.identifier.value = value.slice(0, 64);
    } catch (e) { /* older browsers: no prefill */ }
  };

  CartzyTrack.prototype.links = function (box, withCarrier) {
    var row = el('p', 'cartzy-track__links');
    var carrier = withCarrier ? safeHttpUrl(this.attr('carrier-url')) : null;
    var support = safeHttpUrl(this.attr('support-url'));
    if (carrier) {
      var a = el('a', 'cartzy-track__link', 'India Post tracking');
      a.href = carrier; a.target = '_blank'; a.rel = 'noopener';
      row.appendChild(a);
    }
    if (support) {
      var b = el('a', 'cartzy-track__link', 'Contact us');
      b.href = support; b.target = '_blank'; b.rel = 'noopener';
      row.appendChild(b);
    }
    if (row.childNodes.length) box.appendChild(row);
  };

  // tone: 'attention' (not found / invalid) or 'info' (unavailable / too many
  // attempts). Errors are announced immediately (role=alert).
  CartzyTrack.prototype.showMessage = function (text, tone, withLinks) {
    var box = this.message;
    box.textContent = '';
    box.className = 'cartzy-track__message';
    if (!text) { box.hidden = true; box.setAttribute('role', 'status'); return; }
    box.classList.add('cartzy-track__message--' + (tone || 'info'));
    box.setAttribute('role', 'alert');
    box.appendChild(el('p', 'cartzy-track__message-text', text));
    if (withLinks) this.links(box, true);
    box.hidden = false;
  };

  CartzyTrack.prototype.setBusy = function (busy) {
    this.button.disabled = busy;
    this.form.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.form.elements.identifier.readOnly = busy;
    this.form.elements.phone.readOnly = busy;
    this.root.classList.toggle('cartzy-track--busy', busy);
  };

  CartzyTrack.prototype.markInvalid = function (field, invalid) {
    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
      field.classList.add('cartzy-track__input--invalid');
    } else {
      field.removeAttribute('aria-invalid');
      field.classList.remove('cartzy-track__input--invalid');
    }
  };

  CartzyTrack.prototype.onInput = function (event) {
    if (event.target && event.target.getAttribute('aria-invalid') === 'true') {
      this.markInvalid(event.target, false);
    }
  };

  CartzyTrack.prototype.post = function (body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;
    return fetch(this.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      return response.json().catch(function () { return { status: 'unavailable' }; })
        .then(function (data) { return { http: response.status, data: data || {} }; });
    }, function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  };

  CartzyTrack.prototype.onSubmit = function (event) {
    event.preventDefault();
    var self = this;
    var idField = this.form.elements.identifier;
    var phoneField = this.form.elements.phone;
    var identifier = (idField.value || '').trim();
    var phone = (phoneField.value || '').trim();
    this.result.textContent = '';
    this.markInvalid(idField, !identifier);
    this.markInvalid(phoneField, !phone);
    if (!identifier || !phone) {
      this.showMessage(this.attr('msg-invalid'), 'attention');
      (identifier ? phoneField : idField).focus();
      return;
    }
    this.showMessage('');
    this.setBusy(true);
    var body = { identifier: identifier, phone: phone };
    this.post(body).catch(function () {
      // ONE retry, for a network failure or timeout only.
      return self.post(body);
    }).then(function (reply) {
      self.setBusy(false);
      self.handle(reply.data);
    }, function () {
      self.setBusy(false);
      self.showMessage(self.attr('msg-unavailable'), 'info', true);
    });
  };

  CartzyTrack.prototype.handle = function (data) {
    var status = data && data.status;
    if (status === 'found') { this.render(data); return; }
    if (status === 'not_found') { this.showMessage(this.attr('msg-not-found'), 'attention'); return; }
    if (status === 'rate_limited') { this.showMessage(this.attr('msg-rate-limited'), 'info', true); return; }
    if (status === 'invalid_request') { this.showMessage(this.attr('msg-invalid'), 'attention'); return; }
    this.showMessage(this.attr('msg-unavailable'), 'info', true);
  };

  // Back to the search form. The mobile number is cleared (shared devices);
  // the order / tracking number is kept for a quick retry.
  CartzyTrack.prototype.reset = function () {
    this.result.textContent = '';
    this.form.elements.phone.value = '';
    if (this.intro) this.intro.hidden = false;
    if (this.search) this.search.hidden = false;
    this.showMessage('');
    this.form.elements.identifier.focus();
  };

  // ---------------------------------------------------------------------------
  // Rendering the result.
  // ---------------------------------------------------------------------------
  CartzyTrack.prototype.render = function (data) {
    var self = this;
    var order = data.order || {};
    var shipments = Array.isArray(data.shipments) ? data.shipments : [];
    var cancelled = order.status === 'cancelled';
    var wrap = el('div', 'cartzy-track__result-inner');

    var again = el('button', 'cartzy-track__again', 'Track another order');
    again.type = 'button';
    again.addEventListener('click', function () { self.reset(); });
    wrap.appendChild(again);

    var head = el('div', 'cartzy-track__order-head');
    var heading = el('h2', 'cartzy-track__order-number', order.order_number ? 'Order ' + order.order_number : 'Your order');
    heading.setAttribute('tabindex', '-1');
    head.appendChild(heading);
    if (order.placed_at) head.appendChild(el('p', 'cartzy-track__muted', 'Placed on ' + format(order.placed_at, false)));
    wrap.appendChild(head);

    if (cancelled || !shipments.length) {
      wrap.appendChild(this.renderOrderOnly(order, cancelled));
    } else {
      shipments.forEach(function (shipment) { wrap.appendChild(self.renderSummary(shipment)); });
    }

    var columns = el('div', 'cartzy-track__columns');
    var main = el('div', 'cartzy-track__column cartzy-track__column--main');
    if (!cancelled) {
      var several = shipments.length > 1;
      shipments.forEach(function (shipment, index) { main.appendChild(self.renderHistory(shipment, index, several)); });
    }
    var side = el('div', 'cartzy-track__column cartzy-track__column--side');
    var items = this.renderItems(order);
    if (items) side.appendChild(items);
    if (main.childNodes.length) columns.appendChild(main);
    if (side.childNodes.length) columns.appendChild(side);
    if (columns.childNodes.length) {
      if (columns.childNodes.length === 1) columns.classList.add('cartzy-track__columns--single');
      wrap.appendChild(columns);
    }

    this.result.textContent = '';
    this.result.appendChild(wrap);
    if (this.intro) this.intro.hidden = true;
    if (this.search) this.search.hidden = true;
    this.showMessage('');
    try {
      heading.focus({ preventScroll: true });
    } catch (e) {
      heading.focus();
    }
    if (typeof this.result.scrollIntoView === 'function') {
      this.result.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  };

  CartzyTrack.prototype.statusBlock = function (card, status, label, subline) {
    var pill = el('p', 'cartzy-track__status-title cartzy-track__status--' + status, label);
    card.appendChild(pill);
    if (subline) card.appendChild(el('p', 'cartzy-track__status-sub', subline));
    if (has(NEEDS_CONTACT, status) && safeHttpUrl(this.attr('support-url'))) this.links(card, false);
  };

  CartzyTrack.prototype.renderOrderOnly = function (order, cancelled) {
    var status = cancelled ? 'cancelled' : 'order_received';
    var card = el('section', 'cartzy-track__card cartzy-track__summary cartzy-track--tone-' + toneFor(status));
    card.setAttribute('aria-label', 'Order status');
    this.statusBlock(card, status, this.label(status, order.status_label), SUBLINES[status]);
    if (!cancelled && this.showProgress()) card.appendChild(this.renderProgress(progressForOrderOnly()));
    return card;
  };

  CartzyTrack.prototype.renderSummary = function (shipment) {
    var status = shipment.status;
    var timeline = Array.isArray(shipment.timeline) ? shipment.timeline : [];
    // null = an unsettled parcel whose updates name a final outcome: no
    // stepper and a neutral title; the history below still lists every update.
    var progress = progressFor(status, timeline);
    var card = el('section', 'cartzy-track__card cartzy-track__summary cartzy-track--tone-' + toneFor(status));
    card.setAttribute('aria-label', 'Parcel ' + (shipment.tracking_number || ''));

    var subline = SUBLINES[status] || '';
    if (status === 'delivered' && shipment.delivered_at) subline = 'Delivered on ' + format(shipment.delivered_at, true);
    this.statusBlock(card, status, progress ? this.label(status, shipment.status_label) : UNSETTLED_TITLE, subline);

    var facts = el('dl', 'cartzy-track__facts');
    function fact(term, value, className) {
      if (!value) return;
      var row = el('div', 'cartzy-track__fact' + (className ? ' ' + className : ''));
      row.appendChild(el('dt', null, term));
      row.appendChild(el('dd', null, value));
      facts.appendChild(row);
    }
    fact('Carrier', shipment.carrier);
    fact('Tracking number', shipment.tracking_number, 'cartzy-track__fact--tracking');
    fact('Dispatched', format(shipment.dispatched_at, false));
    if (status === 'delivered' && shipment.delivered_at) fact('Delivered', format(shipment.delivered_at, true));
    else fact('Last update', format(shipment.last_update_at, true));
    card.appendChild(facts);

    if (progress && this.showProgress()) card.appendChild(this.renderProgress(progress));
    return card;
  };

  CartzyTrack.prototype.renderProgress = function (progress) {
    var self = this;
    var list = el('ol', 'cartzy-track__progress cartzy-track__progress--' + progress.journey + ' cartzy-track__progress--' + progress.tone);
    list.setAttribute('aria-label', 'Delivery progress');
    progress.stages.forEach(function (key, index) {
      var state;
      if (index < progress.current || (index === progress.current && progress.complete)) state = 'done';
      else if (index === progress.current) state = 'current';
      else state = 'upcoming';
      var stageItem = el('li', 'cartzy-track__progress-step is-' + state);
      if (index === progress.current) stageItem.setAttribute('aria-current', 'step');
      stageItem.appendChild(el('span', 'cartzy-track__progress-dot'));
      stageItem.appendChild(el('span', 'cartzy-track__progress-label', self.label(key, STAGE_LABELS[key])));
      if (state === 'done') stageItem.appendChild(el('span', 'cartzy-track__visually-hidden', ' (completed)'));
      list.appendChild(stageItem);
    });
    return list;
  };

  // `several`: the order has more than one parcel, so each history names its
  // own tracking number — otherwise identical headings (and identical
  // screen-reader region names) would not say which parcel they belong to.
  CartzyTrack.prototype.renderHistory = function (shipment, index, several) {
    var self = this;
    var card = el('section', 'cartzy-track__card cartzy-track__history');
    var titleId = this.uid + '-history-title-' + index;
    var titleText = several && shipment.tracking_number ? 'Tracking history — ' + shipment.tracking_number : 'Tracking history';
    var title = el('h3', 'cartzy-track__card-title', titleText);
    title.id = titleId;
    card.setAttribute('aria-labelledby', titleId);
    card.appendChild(title);

    // Newest first; every stored update is kept — the older ones are only
    // collapsed until the customer asks for them.
    var timeline = Array.isArray(shipment.timeline) ? shipment.timeline.slice().reverse() : [];
    if (!timeline.length) {
      card.appendChild(el('p', 'cartzy-track__muted', this.attr('msg-no-timeline')));
      return card;
    }
    var visible = this.historyCount();
    var list = el('ol', 'cartzy-track__timeline');
    list.id = this.uid + '-history-' + index;
    var extra = [];
    timeline.forEach(function (step, position) {
      var row = el('li', 'cartzy-track__step cartzy-track__step--' + step.status);
      if (position === 0) row.classList.add('cartzy-track__step--latest');
      if (has(ATTENTION, step.status)) row.classList.add('cartzy-track__step--attention');
      row.appendChild(el('span', 'cartzy-track__step-label', self.label(step.status, step.label)));
      if (step.location) row.appendChild(el('span', 'cartzy-track__step-location', step.location));
      var time = el('time', 'cartzy-track__step-time', format(step.at, true));
      if (step.at) time.setAttribute('datetime', step.at);
      row.appendChild(time);
      if (position >= visible) { row.hidden = true; extra.push(row); }
      list.appendChild(row);
    });
    card.appendChild(list);

    if (extra.length) {
      var toggle = el('button', 'cartzy-track__history-toggle');
      toggle.type = 'button';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', list.id);
      var collapsedText = 'View full tracking history (' + extra.length + ' more)';
      toggle.textContent = collapsedText;
      toggle.addEventListener('click', function () {
        var expand = toggle.getAttribute('aria-expanded') !== 'true';
        extra.forEach(function (row) { row.hidden = !expand; });
        toggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
        toggle.textContent = expand ? 'Show less' : collapsedText;
      });
      card.appendChild(toggle);
    }
    return card;
  };

  CartzyTrack.prototype.renderItems = function (order) {
    var items = Array.isArray(order.items) ? order.items : [];
    if (!items.length) return null;
    var card = el('section', 'cartzy-track__card cartzy-track__items');
    var titleId = this.uid + '-items-title';
    var title = el('h3', 'cartzy-track__card-title', 'Your items');
    title.id = titleId;
    card.setAttribute('aria-labelledby', titleId);
    card.appendChild(title);
    var list = el('ul', 'cartzy-track__item-list');
    items.forEach(function (item) {
      var row = el('li', 'cartzy-track__item');
      var text = el('span', 'cartzy-track__item-text');
      text.appendChild(el('span', 'cartzy-track__item-title', item.title));
      if (item.variant_title) text.appendChild(el('span', 'cartzy-track__item-variant', item.variant_title));
      row.appendChild(text);
      row.appendChild(el('span', 'cartzy-track__item-qty', '× ' + item.quantity));
      list.appendChild(row);
    });
    card.appendChild(list);
    return card;
  };

  function init() {
    var roots = document.querySelectorAll('[data-cartzy-track]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__cartzyTrack) roots[i].__cartzyTrack = new CartzyTrack(roots[i]);
    }
  }

  // The progress rule, exposed read-only so it can be checked on its own.
  window.CartzyTrackOrder = { progressFor: progressFor };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor: re-initialise when the section is added or re-rendered.
  document.addEventListener('shopify:section:load', init);
})();